import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiLayers, FiChevronLeft } from 'react-icons/fi';
import { useAuth } from '../../../context/AuthContext';
import { getAllLedgerAccountsWithInactive, getAccountLedger, getLedgerSummary, exportReportTable } from '../../../services/api';
import { fmtCurrency } from '../../../utils/formatNumber';
import { today } from '../../../utils/dateUtils';
import { getVisibleFinancialYears } from '../../../utils/financialYears';
import PdfOptionsModal from '../../../pdf/PdfOptionsModal';
import { ExcelExportModal, LedgerAccountSelect } from '../../../components/common';
import styles from '../Reports.module.css';

// ── Helpers ────────────────────────────────────────────────────────────────

const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Shows a balance value with a Dr / Cr suffix.
// Positive value = in the account's normal direction (backend already applied sign factor).
const BalanceCell = ({ value, normalBalance }) => {
  const abs     = Math.abs(value ?? 0);
  const isNormal = (value ?? 0) >= 0;
  const label   = isNormal ? normalBalance : (normalBalance === 'Dr' ? 'Cr' : 'Dr');
  const color   = isNormal ? 'var(--color-text-muted)' : 'var(--color-danger)';
  return (
    <span>
      {fmtCurrency(abs)}{' '}
      <span style={{ fontSize: '0.78em', fontWeight: 600, color }}>{label}</span>
    </span>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

export default function AccountLedgerReport({ onExportReady }) {
  const { activeDerasarId, activeDerasar, financialYears } = useAuth();

  const visibleFYs = useMemo(() => getVisibleFinancialYears(financialYears || []), [financialYears]);

  // ── Shared filter state ──
  const [accounts,       setAccounts]       = useState([]);
  const [accountId,      setAccountId]      = useState('');
  const [financialYearId, setFinancialYearId] = useState('');
  const [fromDate,       setFromDate]       = useState(firstOfMonth());
  const [toDate,         setToDate]         = useState(today());

  const selectedFY = useMemo(
    () => visibleFYs.find(fy => String(fy.id) === String(financialYearId)) || null,
    [visibleFYs, financialYearId],
  );
  const fyMinDate = selectedFY ? (selectedFY.startDate || '').slice(0, 10) : '';
  const fyMaxDate = selectedFY ? (selectedFY.endDate   || '').slice(0, 10) : '';

  // ── Single-account mode ──
  const [singleData,    setSingleData]    = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);

  // ── All-ledgers mode ──
  const [mode,        setMode]        = useState('single'); // 'single' | 'all'
  const [allData,     setAllData]     = useState(null);
  const [allLoading,  setAllLoading]  = useState(false);

  // ── Export state ──
  const [showPdfModal,   setShowPdfModal]   = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);

  // Load account list on mount
  useEffect(() => {
    if (!activeDerasarId) return;
    getAllLedgerAccountsWithInactive(activeDerasarId)
      .then((r) => setAccounts((r.data || []).filter((a) => a.isActive)))
      .catch(() => toast.error('Failed to load accounts'));
  }, [activeDerasarId]);

  // Auto-select first FY when list loads
  useEffect(() => {
    if (visibleFYs.length > 0 && !financialYearId) {
      setFinancialYearId(String(visibleFYs[0].id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleFYs]);

  // When FY changes, reset from/to to cover the full FY range
  useEffect(() => {
    if (!selectedFY) return;
    if (fyMinDate) setFromDate(fyMinDate);
    if (fyMaxDate) setToDate(fyMaxDate);
  }, [selectedFY, fyMinDate, fyMaxDate]);

  // ── Single account run ──
  const runSingle = useCallback(async (accId = accountId) => {
    if (!activeDerasarId) { toast.error('Please select a derasar'); return; }
    if (!accId)           { toast.error('Please select an account'); return; }
    setSingleLoading(true);
    setSingleData(null);
    try {
      const res = await getAccountLedger({ derasarId: activeDerasarId, accountId: accId, fromDate, toDate });
      setSingleData(res.data);
      setMode('single');
    } catch {
      toast.error('Failed to load ledger');
    } finally {
      setSingleLoading(false);
    }
  }, [activeDerasarId, accountId, fromDate, toDate]);

  // ── All ledgers run ──
  const runAll = useCallback(async () => {
    if (!activeDerasarId) { toast.error('Please select a derasar'); return; }
    setAllLoading(true);
    setAllData(null);
    try {
      const res = await getLedgerSummary({ derasarId: activeDerasarId, fromDate, toDate });
      setAllData(res.data);
      setMode('all');
    } catch {
      toast.error('Failed to load ledger summary');
    } finally {
      setAllLoading(false);
    }
  }, [activeDerasarId, fromDate, toDate]);

  // Clicking a row in all-ledgers → drill into that account
  const drillInto = (row) => {
    setAccountId(String(row.accountId));
    runSingle(row.accountId);
  };

  const loading = singleLoading || allLoading;

  // ── PDF props (memoised to avoid re-render loop via onExportReady) ──
  const filterStr = `From: ${fromDate}  |  To: ${toDate}`;

  const pdfProps = useMemo(() => {
    if (mode === 'all' && allData) {
      return {
        mode: 'all',
        derasar:  activeDerasar?.derasarId,
        filters:  filterStr,
        groups:   allData.groups || [],
      };
    }
    if (mode === 'single' && singleData) {
      return {
        mode: 'single',
        derasar:        activeDerasar?.derasarId,
        filters:        `${singleData.accountName}  |  ${filterStr}`,
        accountName:    singleData.accountName,
        accountCode:    singleData.accountCode,
        normalBalance:  singleData.normalBalance,
        openingBalance: singleData.openingBalance,
        closingBalance: singleData.closingBalance,
        rows:           singleData.rows || [],
      };
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, allData, singleData, fromDate, toDate]);

  // Notify parent whenever export availability changes
  useEffect(() => {
    if (!onExportReady) return;
    onExportReady(pdfProps ? {
      openPdfModal:   () => setShowPdfModal(true),
      openExcelModal: () => setShowExcelModal(true),
    } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfProps]);

  // ── Excel export ──
  const buildExcelPayload = () => {
    if (mode === 'all' && allData) {
      const rows = (allData.groups || []).flatMap((g) =>
        (g.rows || []).map((r) => ({
          group:          g.name,
          accountName:    r.accountName,
          accountCode:    r.accountCode,
          subGroup:       r.subGroupName || '',
          openingBalance: Number(r.openingBalance || 0),
          openingDir:     r.openingBalance >= 0 ? r.normalBalance : (r.normalBalance === 'Dr' ? 'Cr' : 'Dr'),
          totalDr:        Number(r.totalDr || 0),
          totalCr:        Number(r.totalCr || 0),
          closingBalance: Number(Math.abs(r.closingBalance || 0)),
          closingDir:     r.closingBalance >= 0 ? r.normalBalance : (r.normalBalance === 'Dr' ? 'Cr' : 'Dr'),
        }))
      );
      return {
        title:     'Ledger Balance - All Accounts',
        derasarId: activeDerasarId,
        filters:   filterStr,
        columns: [
          { key: 'group',          label: 'Group',       weight: 1.0 },
          { key: 'accountName',    label: 'Account',     weight: 2.0 },
          { key: 'accountCode',    label: 'Code',        weight: 0.8 },
          { key: 'subGroup',       label: 'Sub-Group',   weight: 1.2 },
          { key: 'openingBalance', label: 'Opening Bal', type: 'currency', align: 'right', weight: 1.0 },
          { key: 'openingDir',     label: 'Dr/Cr',       weight: 0.5 },
          { key: 'totalDr',        label: 'Total Dr',    type: 'currency', align: 'right', weight: 1.0 },
          { key: 'totalCr',        label: 'Total Cr',    type: 'currency', align: 'right', weight: 1.0 },
          { key: 'closingBalance', label: 'Closing Bal', type: 'currency', align: 'right', weight: 1.0 },
          { key: 'closingDir',     label: 'Dr/Cr',       weight: 0.5 },
        ],
        rows,
      };
    }
    if (mode === 'single' && singleData) {
      const entryRows = (singleData.rows || []).map((r) => ({
        date:      r.entryDate  || '',
        voucher:   r.voucherNo  || '',
        payee:     r.payeeName  || '',
        narration: r.narration  || '',
        dr:        Number(r.dr  || 0),
        cr:        Number(r.cr  || 0),
        balance:   Number(Math.abs(r.runningBalance || 0)),
      }));
      const obAbs = Math.abs(singleData.openingBalance || 0);
      const cbAbs = Math.abs(singleData.closingBalance || 0);
      const rows = [
        { date: '', voucher: '', narration: 'Opening Balance', dr: 0, cr: 0, balance: obAbs },
        ...entryRows,
        { date: '', voucher: '', narration: 'Closing Balance', dr: 0, cr: 0, balance: cbAbs },
      ];
      return {
        title:     `Ledger Balance - ${singleData.accountName}`,
        derasarId: activeDerasarId,
        filters:   filterStr,
        columns: [
          { key: 'date',      label: 'Date',           type: 'date', weight: 0.9 },
          { key: 'voucher',   label: 'Voucher No',                      weight: 1.0 },
          { key: 'payee',     label: 'Payee / Member',                  weight: 1.2 },
          { key: 'narration', label: 'Narration',                       weight: 2.2 },
          { key: 'dr',        label: 'Dr (Rs.)',      type: 'currency', align: 'right', weight: 1.0 },
          { key: 'cr',        label: 'Cr (Rs.)',      type: 'currency', align: 'right', weight: 1.0 },
          { key: 'balance',   label: 'Balance (Rs.)', type: 'currency', align: 'right', weight: 1.0 },
        ],
        rows,
        totals: {
          narration: `Total  (${singleData.normalBalance === 'Cr' ? 'Cr' : 'Dr'} normal)`,
          dr: entryRows.reduce((s, r) => s + r.dr, 0),
          cr: entryRows.reduce((s, r) => s + r.cr, 0),
          balance: cbAbs,
        },
      };
    }
    return null;
  };

  const handleDownloadExcel = async () => {
    const payload = buildExcelPayload();
    if (!payload) return;
    try {
      const res = await exportReportTable({ ...payload, format: 'excel' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger-balance-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Failed to export Excel');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Filter bar ── */}
      <div className={styles.filterRow}>
        <div className="form-group" style={{ minWidth: 140 }}>
          <label>Financial Year</label>
          <select value={financialYearId} onChange={e => setFinancialYearId(e.target.value)}>
            <option value="">All years</option>
            {visibleFYs.map(fy => (
              <option key={fy.id} value={fy.id}>{fy.yearLabel}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>From Date</label>
          <input
            type="date"
            value={fromDate}
            min={fyMinDate || undefined}
            max={fyMaxDate || undefined}
            onChange={e => setFromDate(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>To Date</label>
          <input
            type="date"
            value={toDate}
            min={fyMinDate || undefined}
            max={fyMaxDate || undefined}
            onChange={e => setToDate(e.target.value)}
          />
        </div>

        {mode === 'single' && (
          <div className="form-group" style={{ minWidth: 220, maxWidth: 320 }}>
            <label>Account *</label>
            <LedgerAccountSelect
              value={accountId}
              onChange={setAccountId}
              accounts={accounts}
              placeholder="— Select account —"
            />
          </div>
        )}

        <div className="form-group" style={{ alignSelf: 'flex-end', display: 'flex', gap: 8 }}>
          {mode === 'single' ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => runSingle()} disabled={loading}>
                {singleLoading ? 'Loading…' : 'Run'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={runAll} disabled={loading}>
                <FiLayers size={13} /> All Ledgers
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setMode('single')} disabled={loading}>
                <FiChevronLeft size={13} /> Single Account
              </button>
              <button className="btn btn-primary btn-sm" onClick={runAll} disabled={loading}>
                {allLoading ? 'Loading…' : 'Refresh'}
              </button>
            </>
          )}
        </div>
      </div>

      {loading && <div className="loading"><div className="spinner" /></div>}

      {/* ── All Ledgers view ── */}
      {mode === 'all' && allData && (
        <AllLedgersView groups={allData.groups || []} onDrillInto={drillInto} />
      )}

      {/* ── Single Account detail view ── */}
      {mode === 'single' && singleData && (
        <SingleLedgerView data={singleData} />
      )}

      {showPdfModal && pdfProps && (
        <PdfOptionsModal
          type="ledgerBalance"
          documentProps={pdfProps}
          onClose={() => setShowPdfModal(false)}
        />
      )}

      {showExcelModal && (
        <ExcelExportModal
          onClose={() => setShowExcelModal(false)}
          onDownload={handleDownloadExcel}
          exportPayload={buildExcelPayload() || {}}
        />
      )}
    </div>
  );
}

// ── All Ledgers view ───────────────────────────────────────────────────────

function AllLedgersView({ groups, onDrillInto }) {
  const safeGroups = Array.isArray(groups) ? groups : [];
  const grandDr  = safeGroups.flatMap((g) => g.rows || []).reduce((s, r) => s + (r.totalDr || 0), 0);
  const grandCr  = safeGroups.flatMap((g) => g.rows || []).reduce((s, r) => s + (r.totalCr || 0), 0);

  return (
    <div className="card">
      <div className="card-header">
        <h3>All Ledger Accounts</h3>
        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
          Click any account to view its detailed transactions
        </span>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Sub-Group</th>
              <th className={styles.amtRight}>Opening Bal (₹)</th>
              <th className={styles.amtRight}>Total Debit (₹)</th>
              <th className={styles.amtRight}>Total Credit (₹)</th>
              <th className={styles.amtRight}>Closing Bal (₹)</th>
            </tr>
          </thead>
          <tbody>
            {safeGroups.map((group) => {
              const gOpenDr  = group.rows.reduce((s, r) => s + (r.openingBalance  >= 0 ? r.openingBalance  : 0), 0);
              const gOpenCr  = group.rows.reduce((s, r) => s + (r.openingBalance  < 0  ? Math.abs(r.openingBalance) : 0), 0);
              const gDr      = group.rows.reduce((s, r) => s + r.totalDr, 0);
              const gCr      = group.rows.reduce((s, r) => s + r.totalCr, 0);
              const gClosDr  = group.rows.reduce((s, r) => s + (r.closingBalance  >= 0 ? r.closingBalance  : 0), 0);
              const gClosCr  = group.rows.reduce((s, r) => s + (r.closingBalance  < 0  ? Math.abs(r.closingBalance) : 0), 0);

              return [
                // Group header row
                <tr key={`g-${group.name}`}
                    style={{ background: 'var(--color-primary-bg)', fontWeight: 700 }}>
                  <td colSpan={2} style={{ color: 'var(--color-primary)', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {group.name}
                  </td>
                  <td className={styles.amtRight} style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    {gOpenDr > 0 ? fmtCurrency(gOpenDr) + ' Dr' : gOpenCr > 0 ? fmtCurrency(gOpenCr) + ' Cr' : '—'}
                  </td>
                  <td className={styles.amtRight} style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    {fmtCurrency(gDr)}
                  </td>
                  <td className={styles.amtRight} style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    {fmtCurrency(gCr)}
                  </td>
                  <td className={styles.amtRight} style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    {gClosDr > 0 ? fmtCurrency(gClosDr) + ' Dr' : gClosCr > 0 ? fmtCurrency(gClosCr) + ' Cr' : '—'}
                  </td>
                </tr>,
                // Account rows
                ...group.rows.map((row) => (
                  <tr key={row.accountId}
                      onClick={() => onDrillInto(row)}
                      style={{ cursor: 'pointer' }}
                      className="table-row-hover">
                    <td style={{ paddingLeft: 20 }}>{row.accountName}</td>
                    <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem' }}>
                      {row.subGroupName || '—'}
                    </td>
                    <td className={styles.amtRight}>
                      <BalanceCell value={row.openingBalance} normalBalance={row.normalBalance} />
                    </td>
                    <td className={styles.amtRight}>
                      {row.totalDr > 0 ? fmtCurrency(row.totalDr) : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                    </td>
                    <td className={styles.amtRight}>
                      {row.totalCr > 0 ? fmtCurrency(row.totalCr) : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                    </td>
                    <td className={styles.amtRight}>
                      <BalanceCell value={row.closingBalance} normalBalance={row.normalBalance} />
                    </td>
                  </tr>
                )),
              ];
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}><strong>Grand Total</strong></td>
              <td className={styles.amtRight}><strong>{fmtCurrency(grandDr)}</strong></td>
              <td className={styles.amtRight}><strong>{fmtCurrency(grandCr)}</strong></td>
              <td className={styles.amtRight} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Single Account detail view ─────────────────────────────────────────────

const fmtDateDMY = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const parts = s.split('-');
  if (parts.length !== 3) return s;
  const [y, m, day] = parts;
  return `${day}-${m}-${y}`;
};

function SingleLedgerView({ data }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>{data.accountName}</h3>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher No</th>
              <th>Payee / Member</th>
              <th>Narration</th>
              <th>Contra Account</th>
              <th className={styles.amtRight}>Debit (₹)</th>
              <th className={styles.amtRight}>Credit (₹)</th>
              <th className={styles.amtRight}>Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: 'var(--color-surface-raised)' }}>
              <td colSpan={7}><em>Opening Balance</em></td>
              <td className={styles.amtRight}>
                <em><BalanceCell value={data.openingBalance} normalBalance={data.normalBalance} /></em>
              </td>
            </tr>
            {data.rows?.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  No transactions in this period.
                </td>
              </tr>
            )}
            {data.rows?.map((r, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDateDMY(r.entryDate)}</td>
                <td><code>{r.voucherNo}</code></td>
                <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{r.payeeName || '—'}</td>
                <td>{r.narration}</td>
                <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem' }}>{r.contraAccounts || '—'}</td>
                <td className={styles.amtRight}>{r.dr > 0 ? fmtCurrency(r.dr) : '—'}</td>
                <td className={styles.amtRight}>{r.cr > 0 ? fmtCurrency(r.cr) : '—'}</td>
                <td className={styles.amtRight}>
                  <BalanceCell value={r.runningBalance} normalBalance={data.normalBalance} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7}><strong>Closing Balance</strong></td>
              <td className={styles.amtRight}>
                <strong><BalanceCell value={data.closingBalance} normalBalance={data.normalBalance} /></strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
